import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Shield, Search, Eye, Calendar, DollarSign, 
  AlertCircle, CheckCircle, XCircle, Clock, FileText, Upload,
  User, MapPin, Building, Phone, Mail, ChevronRight, LayoutGrid, List
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';

const ClaimsList = () => {
  const { user } = useAuth();
  const [claims, setClaims] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [viewMode, setViewMode] = useState('cards'); // 'cards' or 'table'

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  useEffect(() => {
    loadClaims();
  }, [filterStatus]);

  const loadClaims = async () => {
    if (!user?.id) return;
    
    setIsLoading(true);
    try {
      const params = { 
        user_id: user.id,
        _t: Date.now()
      };
      if (filterStatus !== 'all') {
        params.status = filterStatus;
      }

      const response = await axios.get(`${BACKEND_URL}/api/claims`, { params });
      setClaims(response.data);
    } catch (error) {
      console.error('Error loading claims:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusConfig = (status) => {
    const statusConfig = {
      draft: { icon: Clock, color: 'bg-gray-100 text-gray-700 border-gray-300', label: 'Draft', dotColor: 'bg-gray-400' },
      submitted: { icon: FileText, color: 'bg-blue-50 text-blue-700 border-blue-200', label: 'Submitted', dotColor: 'bg-blue-500' },
      pending_review: { icon: Clock, color: 'bg-amber-50 text-amber-700 border-amber-200', label: 'Pending Review', dotColor: 'bg-amber-500' },
      submit_supplement: { icon: Upload, color: 'bg-purple-50 text-purple-700 border-purple-200', label: 'Submit Supplement', dotColor: 'bg-purple-500' },
      supplement_submitted: { icon: FileText, color: 'bg-violet-50 text-violet-700 border-violet-200', label: 'Supplement Submitted', dotColor: 'bg-violet-500' },
      under_review: { icon: Clock, color: 'bg-yellow-50 text-yellow-700 border-yellow-200', label: 'Under Review', dotColor: 'bg-yellow-500' },
      approved: { icon: CheckCircle, color: 'bg-green-50 text-green-700 border-green-200', label: 'Approved', dotColor: 'bg-green-500' },
      denied: { icon: XCircle, color: 'bg-red-50 text-red-700 border-red-200', label: 'Denied', dotColor: 'bg-red-500' },
      paid: { icon: DollarSign, color: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: 'Paid', dotColor: 'bg-emerald-500' }
    };
    return statusConfig[status] || statusConfig.draft;
  };

  const getStatusBadge = (status) => {
    const config = getStatusConfig(status);
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold border ${config.color}`}>
        <span className={`w-2 h-2 rounded-full mr-2 ${config.dotColor}`}></span>
        {config.label}
      </span>
    );
  };

  const filteredClaims = claims.filter(claim => {
    const searchLower = searchTerm.toLowerCase();
    const customerName = `${claim.estimate?.customer_first_name || ''} ${claim.estimate?.customer_last_name || ''}`.toLowerCase();
    return (
      customerName.includes(searchLower) ||
      claim.claim_number?.toLowerCase().includes(searchLower) ||
      claim.insurance_company?.toLowerCase().includes(searchLower) ||
      claim.estimate?.address?.toLowerCase().includes(searchLower)
    );
  });

  const stats = {
    total: claims.length,
    pending: claims.filter(c => ['submitted', 'pending_review', 'under_review'].includes(c.status)).length,
    approved: claims.filter(c => c.status === 'approved').length,
    paid: claims.filter(c => c.status === 'paid').length,
    totalValue: claims.reduce((sum, c) => sum + (c.covered_work_amount || 0), 0)
  };

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading claims...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <Shield className="w-8 h-8 mr-3 text-orange-600" />
            Insurance Claims
          </h1>
          <p className="text-gray-600 mt-1">Manage and track insurance claims</p>
        </div>
        <Link
          to="/dashboard/claims/new"
          className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded-lg font-semibold flex items-center transition-colors shadow-sm"
          data-testid="new-claim-btn"
        >
          <Shield className="w-5 h-5 mr-2" />
          New Claim
        </Link>
      </div>

      {/* Stats Cards - Acculynx Style */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Total Claims</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{stats.total}</p>
            </div>
            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
              <Shield className="w-6 h-6 text-gray-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">In Progress</p>
              <p className="text-3xl font-bold text-blue-600 mt-1">{stats.pending}</p>
            </div>
            <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center">
              <Clock className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Approved</p>
              <p className="text-3xl font-bold text-green-600 mt-1">{stats.approved}</p>
            </div>
            <div className="w-12 h-12 bg-green-50 rounded-full flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Paid Out</p>
              <p className="text-3xl font-bold text-emerald-600 mt-1">{stats.paid}</p>
            </div>
            <div className="w-12 h-12 bg-emerald-50 rounded-full flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-emerald-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Total Value</p>
              <p className="text-2xl font-bold text-orange-600 mt-1">{formatCurrency(stats.totalValue)}</p>
            </div>
            <div className="w-12 h-12 bg-orange-50 rounded-full flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-orange-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters & View Toggle */}
      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
        <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
          <div className="flex-1 w-full lg:max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                type="text"
                placeholder="Search by client name, claim #, or address..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-gray-50 border-gray-200 focus:bg-white"
                data-testid="claims-search"
              />
            </div>
          </div>

          <div className="flex gap-2 flex-wrap justify-center">
            {['all', 'submitted', 'pending_review', 'approved', 'paid'].map((status) => (
              <button
                key={status}
                onClick={() => setFilterStatus(status)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  filterStatus === status
                    ? 'bg-orange-600 text-white shadow-sm'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                data-testid={`filter-${status}`}
              >
                {status === 'all' ? 'All' : status === 'pending_review' ? 'Pending' : status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>

          <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
            <button
              onClick={() => setViewMode('cards')}
              className={`p-2 rounded ${viewMode === 'cards' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'}`}
              title="Card View"
            >
              <LayoutGrid className="w-5 h-5 text-gray-600" />
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`p-2 rounded ${viewMode === 'table' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'}`}
              title="Table View"
            >
              <List className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>
      </div>

      {/* Claims List */}
      {filteredClaims.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
          <Shield className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">No Claims Found</h3>
          <p className="text-gray-500 mb-6">
            {searchTerm ? `No claims match "${searchTerm}"` : 'Start by creating your first insurance claim'}
          </p>
          <Link
            to="/dashboard/claims/new"
            className="inline-flex items-center bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
          >
            <Shield className="w-5 h-5 mr-2" />
            Create New Claim
          </Link>
        </div>
      ) : viewMode === 'cards' ? (
        /* Card View - Acculynx Style */
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredClaims.map((claim) => {
            const customerName = `${claim.customer_first_name || claim.estimate?.customer_first_name || ''} ${claim.customer_last_name || claim.estimate?.customer_last_name || ''}`.trim() || 'Unknown Client';
            const claimAddress = claim.property_address || claim.estimate?.address || 'No address';
            const statusConfig = getStatusConfig(claim.status);
            
            return (
              <Link
                key={claim.id}
                to={`/dashboard/claims/${claim.id}`}
                className="bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-lg hover:border-orange-200 transition-all group"
                data-testid={`claim-card-${claim.id}`}
              >
                {/* Client Header */}
                <div className="p-5 border-b border-gray-100">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-sm">
                        {customerName.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 text-lg group-hover:text-orange-600 transition-colors">
                          {customerName}
                        </h3>
                        <p className="text-sm text-gray-500 flex items-center mt-0.5">
                          <MapPin className="w-3.5 h-3.5 mr-1" />
                          {claimAddress.split(',')[0]}
                        </p>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-orange-600 group-hover:translate-x-1 transition-all" />
                  </div>
                </div>

                {/* Claim Details */}
                <div className="p-5 space-y-4">
                  {/* Status & Claim # */}
                  <div className="flex items-center justify-between">
                    {getStatusBadge(claim.status)}
                    <span className="text-sm font-mono text-gray-500">
                      #{claim.claim_number || 'Pending'}
                    </span>
                  </div>

                  {/* Insurance Info */}
                  <div className="flex items-center gap-3 text-sm">
                    <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
                      <Building className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{claim.insurance_company || 'Insurance Company'}</p>
                      {claim.adjuster?.name && (
                        <p className="text-gray-500 text-xs">Adjuster: {claim.adjuster.name}</p>
                      )}
                    </div>
                  </div>

                  {/* Financial Summary */}
                  <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Claim Value</span>
                      <span className="font-semibold text-gray-900">
                        {claim.covered_work_amount ? formatCurrency(claim.covered_work_amount) : 'TBD'}
                      </span>
                    </div>
                    {claim.insurance_payment > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Paid Amount</span>
                        <span className="font-semibold text-green-600">
                          {formatCurrency(claim.insurance_payment)}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Footer */}
                  <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                    <span className="text-xs text-gray-500 flex items-center">
                      <Calendar className="w-3.5 h-3.5 mr-1" />
                      {formatDate(claim.created_at)}
                    </span>
                    {claim.damage_percentage && (
                      <span className="text-xs font-medium text-orange-600 bg-orange-50 px-2 py-1 rounded">
                        {claim.damage_percentage}% damage
                      </span>
                    )}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      ) : (
        /* Table View */
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Client
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Claim Info
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Insurance
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {filteredClaims.map((claim) => {
                  const customerName = `${claim.customer_first_name || claim.estimate?.customer_first_name || ''} ${claim.customer_last_name || claim.estimate?.customer_last_name || ''}`.trim() || 'Unknown';
                  const claimAddress = claim.property_address || claim.estimate?.address || 'No address';
                  
                  return (
                    <tr key={claim.id} className="hover:bg-gray-50 transition-colors" data-testid={`claim-row-${claim.id}`}>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center text-white font-bold">
                            {customerName.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div className="font-semibold text-gray-900">{customerName}</div>
                            <div className="text-sm text-gray-500 flex items-center">
                              <MapPin className="w-3 h-3 mr-1" />
                              {claimAddress.split(',')[0]}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-mono text-gray-900">
                          #{claim.claim_number || 'Pending'}
                        </div>
                        <div className="text-xs text-gray-500">
                          {formatDate(claim.created_at)}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">
                          {claim.insurance_company || 'N/A'}
                        </div>
                        {claim.adjuster?.name && (
                          <div className="text-xs text-gray-500">
                            {claim.adjuster.name}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {claim.insurance_payment ? (
                          <div>
                            <div className="text-sm font-semibold text-green-600">
                              {formatCurrency(claim.insurance_payment)}
                            </div>
                            <div className="text-xs text-gray-500">Paid</div>
                          </div>
                        ) : claim.covered_work_amount ? (
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {formatCurrency(claim.covered_work_amount)}
                            </div>
                            <div className="text-xs text-gray-500">Est. Value</div>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">TBD</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {getStatusBadge(claim.status)}
                      </td>
                      <td className="px-6 py-4">
                        <Link
                          to={`/dashboard/claims/${claim.id}`}
                          className="inline-flex items-center text-orange-600 hover:text-orange-800 text-sm font-medium"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClaimsList;
